import { useState } from 'react';
import { FaChevronDown, FaExternalLinkAlt, FaGithub } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import TechStack from './TechStack';

function ProjectCard({
  name,
  description,
  githubUrl,
  projectUrl,
  images,
  technologies,
  featured,
  collapsible = true,
  defaultExpanded = false,
  centerContent = false,
  imageFill = false,
  imageFrameClass = 'h-64 sm:h-80 md:h-96',
  animationDelay
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const expanded = collapsible ? isExpanded : true;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const previousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <article
      className={`glass-panel overflow-hidden transition-all duration-300 ${
        featured ? 'ring-1 ring-emerald-300/30' : ''
      }`}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <div
        className={`flex w-full items-center gap-4 bg-gradient-to-r from-emerald-500/8 via-transparent to-emerald-300/10 px-4 py-4 text-left transition-colors duration-300 sm:px-5 ${
          collapsible ? 'cursor-pointer justify-between hover:bg-emerald-500/10' : 'justify-center'
        }`}
        onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsExpanded((prev) => !prev);
                }
              }
            : undefined
        }
        aria-expanded={collapsible ? expanded : undefined}
      >
        <div className={centerContent ? 'mx-auto text-center' : ''}>
          <h3 className="text-xl font-semibold text-emerald-50 sm:text-2xl">{name}</h3>
        </div>
        {collapsible && (
          <FaChevronDown
            className={`shrink-0 text-emerald-200 transition-transform duration-300 ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>

      <div
        className={`transition-all duration-300 ease-in-out ${
          expanded ? 'max-h-[1600px] opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}
      >
        <div className={`border-t border-emerald-300/15 px-4 py-4 sm:px-5 ${centerContent ? 'text-center' : ''}`}>
          <p className="mb-4 text-sm leading-relaxed text-emerald-100 sm:text-base">{description}</p>

          {technologies && technologies.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-emerald-200/90">
                Technologies
              </h4>
              <TechStack technologies={technologies} className={centerContent ? 'justify-center' : ''} />
            </div>
          )}

          <div className={`flex flex-wrap gap-3 ${centerContent ? 'justify-center' : ''}`}>
            {projectUrl && (
              <a
                href={projectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-500/30"
              >
                <FaExternalLinkAlt className="text-xs" />
                View Project
              </a>
            )}
            <a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-100 transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-500/20"
            >
              <FaGithub />
              View on GitHub
            </a>
          </div>
        </div>

        {images.length > 0 && (
          <div className="relative border-t border-emerald-300/15 p-4 sm:p-5">
            <div className={`relative overflow-hidden rounded-2xl border border-emerald-300/20 bg-emerald-950/80 ${imageFrameClass}`}>
              <div className="absolute w-full h-full">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300 flex items-center justify-center ${
                      index === currentImageIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${name} screenshot ${index + 1}`}
                      className={`h-full w-full ${imageFill ? 'object-cover' : 'object-contain'}`}
                    />
                  </div>
                ))}
              </div>

              {images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      previousImage();
                    }}
                    className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full border border-emerald-200/25 bg-emerald-900/70 p-2.5 transition-colors hover:bg-emerald-800"
                    aria-label="Previous project image"
                  >
                    <BsChevronLeft className="text-emerald-100" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full border border-emerald-200/25 bg-emerald-900/70 p-2.5 transition-colors hover:bg-emerald-800"
                    aria-label="Next project image"
                  >
                    <BsChevronRight className="text-emerald-100" />
                  </button>
                </>
              )}

              {images.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2 z-20">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        currentImageIndex === index
                          ? 'bg-emerald-300'
                          : 'bg-emerald-700/90 hover:bg-emerald-600'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export default ProjectCard; 